import React from 'react'
import PropTypes from 'prop-types'
import { FormattedNumber, BarChart, Tooltip } from 'belle-js'
import InfoTooltip from '../InfoTooltip/InfoTooltip'
import tooltipStyles from '../InfoTooltip/InfoTooltip.module.scss'

import {
  allClassAllocations,
  UNASSIGNED_ASSETS,
} from 'utils/Client/classAllocationsConfig.js'
import {
  MARKET_VALUE,
  MARKET_WEIGHT,
  ASSET_NAME,
} from 'utils/Client/holdingsConstants.js'
import Big from 'big.js'
import classnames from 'classnames'

import styles from './HoldingsList.module.scss'

class HoldingsListColumn extends React.Component {

  constructor (props) {
    super(props)
    this.handleToggleTooltip = this.handleToggleTooltip.bind(this)
    this.holding = React.createRef()
    this.percentage = React.createRef()
    this.state = {
      displayTextTooltip: false,
      isTextTruncated: false,
      percentageWidth: 50,
    }
  }

  componentDidMount () {
    const {
      current
    } = this.holding

    /* eslint-disable react/no-did-mount-set-state */
    if (this.percentage) {
      this.percentage.current && this.setState({
        percentageWidth: this.percentage.current.offsetWidth
      })
    }

    if (current) {
      if (current.scrollWidth > current.clientWidth) {
        this.setState({
          isTextTruncated: true
        })
      }
    }
  }

  handleToggleTooltip () {
    this.setState(prevState => ({
      displayTextTooltip: !prevState.displayTextTooltip
    }))
  }


  getMarketValue () {
    const {
      value,
      classAllocationName,
      isHeaderRow,
      isFooterRow,
    } = this.props
    const nameStyles = classnames({
      [styles.uppercase]: isHeaderRow,
    })

    let marketValue = (
      <FormattedNumber
        value={value}
        format="currencyWithDecimal"
        showNegativeSign={false} />
    )

    if (isHeaderRow) {
      if (classAllocationName === UNASSIGNED_ASSETS.name) {
        const unassignedText = 'What\'s this?'
        return (
          <InfoTooltip className={styles.tooltipWrapper} title={unassignedText}>
            <div className={tooltipStyles.tooltipContent}>
              <p className={tooltipStyles.header}>YOUR HOLDINGS ARE UNDER REVIEW</p>
              <p className={tooltipStyles.body}>Our investments team is currently reviewing
              these holdings and classifying them into the appropriate asset classes.
              When our review is complete,
              your holdings will be reflected under the corresponding asset class heading.
              </p>
            </div>
          </InfoTooltip>
        )
      } else {
        return  <span className={nameStyles}>{value}</span>
      }
    } else if (isFooterRow) {
      marketValue = (
        <React.Fragment>
          <div className={styles.totalMarketValue}>
          Total
          </div>
          <div>
            {marketValue}
          </div>
        </React.Fragment>)
    }
    return marketValue
  }

  getMarketWeight () {
    const barWidth = 368
    const barHeight = 23.8
    const {
      value,
      classAllocationName,
      isHeaderRow,
      isFooterRow,
      isMobile,
    } = this.props
    const nameStyles = classnames({
      [styles.uppercase]: isHeaderRow,
    })
    const classAllocationConfig = allClassAllocations.find(c => c.name === classAllocationName)

    if (isHeaderRow || isFooterRow) {
      return <span className={nameStyles}>{value}</span>
    } else if (!isMobile) {
      const marketWeight = new Big(value).div(new Big(100))
      const chartColor = classAllocationConfig ?
        classAllocationConfig.color : UNASSIGNED_ASSETS.color
      const chartData = [{
        value: marketWeight,
        fill: chartColor,
        label: `${value}%`,
      }]
      const inThresholdPercentage = marketWeight.gte(new Big(0.85))

      if (inThresholdPercentage) {
        const { percentageWidth } = this.state
        const paddingRight = '10px'
        const filledBarWidth = chartData[0].value.times(new Big(barWidth)).toString()
        const inlineStyles = {
          left: `calc(${filledBarWidth}px - ${percentageWidth}px - ${paddingRight})`,
        }
        const percentageLabel = `${marketWeight.times(new Big(100)).toString()}%`

        return marketWeight && (
          <React.Fragment>
            <BarChart
              width={barWidth}
              height={barHeight}
              useHtmlLabels={true}
              data={chartData} />
            <span
              ref={this.percentage}
              style={inlineStyles}
              className={styles.barChartPercentage}>
              {percentageLabel}
            </span>
          </React.Fragment>
        )
      } else {
        return marketWeight && (
          <BarChart
            width={barWidth}
            height={barHeight}
            data={chartData} />
        )
      }
    } else {
      return <span className={nameStyles}>{`${value}%`}</span>
    }
  }

  renderListItem () {
    const {
      value,
      type,
      isHeaderRow,
    } = this.props
    let item
    const nameStyles = classnames({
      [styles.uppercase]: isHeaderRow,
    })

    switch (type) {
      case MARKET_VALUE:
        item = this.getMarketValue()
        break
      case MARKET_WEIGHT:
        item = this.getMarketWeight()
        break
      case ASSET_NAME:
      default:
        item = (
          <div ref={this.holding} className={styles.displayItemDots}>
            <span
              className={nameStyles}
              onMouseEnter={this.handleToggleTooltip}
              onMouseLeave={this.handleToggleTooltip}>
              {value}
            </span>
          </div>
        )
        break
    }
    return item
  }

  render () {
    const {
      value,
      className,
      type,
      isMobile,
    } = this.props
    const {
      displayTextTooltip,
      isTextTruncated,
    } = this.state
    const shouldDisplayTooltip = type === ASSET_NAME &&
      displayTextTooltip && isTextTruncated && !isMobile
    const tooltip = shouldDisplayTooltip &&
      <span className={styles.textTooltipPosition}>
        <Tooltip
          closeButton={false}
          toggleButton={false}
          isOpen={displayTextTooltip}>
          {value}
        </Tooltip>
      </span>

    return (
      <div className={className}>
        {this.renderListItem()}
        {tooltip}
      </div>
    )
  }

}

HoldingsListColumn.defaultProps = {
  value: '',
  className: '',
  classAllocationName: '',
  type: '',
  isHeaderRow: false,
  isFooterRow: false,
  isMobile: false,
}

HoldingsListColumn.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  className: PropTypes.string,
  classAllocationName: PropTypes.string,
  type: PropTypes.string,
  isHeaderRow: PropTypes.bool,
  isFooterRow: PropTypes.bool,
  isMobile: PropTypes.bool,
}

export default HoldingsListColumn



// WEBPACK FOOTER //
// ./src/components/Client/HoldingsList/HoldingsListColumn.jsx